import { Injectable, NgZone } from "@angular/core";
import posthog from "posthog-js";
import { EnvironmentService } from "./environment.service";
import { UsersService } from "./users.service";

/** Runtime config shape exposed under the `posthog` key of `/api/config` / environment files. */
interface PosthogEnvironmentConfig {
  api_key?: string;
  host?: string;
  enabled?: boolean;
}

const DEFAULT_HOST = "https://us.i.posthog.com";

/**
 * PostHog product analytics (events, autocapture, heatmaps, session replay).
 *
 * Coexists with AnalyticsService (GTM + GA4): call sites that need PostHog inject
 * this service and use `capture()`. Apps must call `init()` once the runtime
 * config is available (team does it from its APP_INITIALIZER); a missing or
 * disabled config makes every method a silent no-op, so shared components can
 * capture unconditionally even when the host app does not enable PostHog.
 */
@Injectable({
  providedIn: "root",
})
export class PosthogService {
  private is_initialized = false;
  private identified_user_id: string | null = null;

  constructor(private ngZone: NgZone, private _envService: EnvironmentService, private _usersService: UsersService) {}

  /** Initializes posthog-js from the `posthog` runtime config block. Safe to call more than once. */
  init(): void {
    if (this.is_initialized) {
      return;
    }
    const config: PosthogEnvironmentConfig = this._envService.get("posthog") || {};
    if (!config.enabled) {
      return;
    }
    if (!config.api_key) {
      if (!this._envService.get("production")) {
        console.error(
          "POSTHOG_API_KEY variable required by PostHog is missing or un-configured, this causes events " +
            "to be silently missed. This error stops appearing once POSTHOG_API_KEY is configured"
        );
      }
      return;
    }
    // Outside the Angular zone so autocapture/session replay don't trigger change detection.
    this.ngZone.runOutsideAngular(() => {
      posthog.init(config.api_key as string, {
        api_host: config.host || DEFAULT_HOST,
        defaults: "2026-05-30",
        capture_exceptions: true,
        session_recording: {
          maskAllInputs: true,
        },
      });
    });
    this.is_initialized = true;
    // Logout must clear the identity immediately: autocapture and session replay keep
    // running without custom captures, so waiting for the next capture() would keep
    // attributing activity to the previous user.
    this._usersService.registerLogoutCallback(() => this.reset());
    this.syncIdentity();
  }

  /** Captures a custom event. No-op when PostHog is disabled or not configured. */
  capture(event_name: string, properties?: Record<string, unknown>): void {
    if (!this.is_initialized) {
      return;
    }
    this.syncIdentity();
    this.ngZone.runOutsideAngular(() => posthog.capture(event_name, properties));
  }

  /** Clears the identified person (call on logout when captures might follow before a new login). */
  reset(): void {
    if (!this.is_initialized) {
      return;
    }
    this.ngZone.runOutsideAngular(() => posthog.reset());
    this.identified_user_id = null;
  }

  /**
   * Keeps the PostHog person in sync with the logged-in user. Reads the user lazily on every
   * capture (never a constructor snapshot) so login/logout/user-switch can't mis-attribute
   * events: a different user resets the previous identity before identifying the new one.
   */
  private syncIdentity(): void {
    const user = this._usersService.getCurrentUser();
    const user_id = user?.id != null ? String(user.id) : null;
    if (user_id === this.identified_user_id) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      if (this.identified_user_id) {
        posthog.reset();
      }
      if (user_id) {
        posthog.identify(user_id, { name: user.full_name, tipo: user.tipo });
      }
    });
    this.identified_user_id = user_id;
  }
}
